import React from "react";
import { useDropzone } from "react-dropzone";
import { METAVERSE_FOLDER, getMediaType, uploadXmlHTTP } from "../../utils/media-utils";
import { getCookieToken } from "../../utils/get-auth-user";
import configs from "../../utils/configs";
import { processThumbnail } from "../../utils/thumbnails";
import { getSignedUrl } from "../../utils/artwork-utils";
import { makeStyles, ProgressBar } from "@fluentui/react-components";
import { ArrowUploadFilled } from "@fluentui/react-icons";
import { useUploadFormContext } from "../contexts/UploadFormContext";

const useStyles = makeStyles({
  dropzoneCard: {
    display: "flex",
    width: "100%",
    "@media (min-width: 576px)": {
      width: "400px",
      margin: "auto"
    },
    height: "300px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "var(--card-corner-radius, 4px)",
    border: "1px dashed var(--primary)",
    background: "var(--primary-color-background)",
    boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.14), 0px 0px 2px 0px rgba(0, 0, 0, 0.12)",
    "&:hover": {
      border: "1px solid var(--primary)",
      cursor: "pointer"
    }
  },
  dropzoneContent: {
    display: " flex",
    flexDirection: " column",
    alignItems: " center",
    justifyContent: "center",
    gap: "10px",
    height: "100%",
    width: "100%"
  },
  dropzoneContentUpload: {
    display: " flex",
    maxWidth: "227px",
    display: " flex",
    flexDirection: " column",
    alignItems: " center",
    justifyContent: "center",
    gap: "10px",
    height: "100%",
    width: "100%"
  },
  firstText: {
    color: "var(--Gray-4, #BDBDBD)",
    textAlign: "center",
    fontSize: "var(--Font-size-300, 14px)",
    fontWeight: 600
  },
  secondText: {
    color: "var(--Gray-3, #828282)",
    fontFamily: "var(--Font-family-Base, 'Segoe UI')",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal"
  },
  uploadIcon: {
    width: "40px",
    minWidth: "40px",
    height: "40px",
    "& path": {
      fill: "var(--colorBrandBackground)",
      color: "var(--colorBrandBackground)"
    }
  }
});

function NewDropzone({ onFileLoaded }) {
  const styles = useStyles();
  const { indefiniteLoading, setIndefiniteLoading, errorUpload, setErrorUpload } = useUploadFormContext();

  const [progressLoading, setProgressLoading] = React.useState(0);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async acceptedFiles => {
      try {
        setErrorUpload(false);
        const acceptedFile = acceptedFiles[0];

        // List of accepted file extensions
        const acceptedExtensions = ["glb", "png", "jpg", "jpeg", "gif", "pdf", "mp4", "mp3"];

        // Get the file extension
        const fileExtension = acceptedFile.name.split(".").pop().toLowerCase();

        // Check if the file extension is in the list of accepted extensions
        if (!acceptedExtensions.includes(fileExtension)) {
          alert("File type not supported. Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files are allowed.");
          return;
        }

        // Check if the file size is more than 1000MB
        if (acceptedFile.size > 1000 * 1024 * 1024) {
          alert("File is too large. Maximum file size is 1000MB.");
          return;
        }

        setIndefiniteLoading(true);

        const blob = acceptedFile.slice(0, acceptedFile.size, acceptedFile.type);
        const newFile = new File([blob], acceptedFile.name, { type: acceptedFile.type });
        const onProgress = progress => {
          setIndefiniteLoading(false);
          setProgressLoading(progress / 100);
        };

        const fileResponse = await uploadXmlHTTP(newFile, acceptedFile?.type || "application/octet-stream", onProgress);

        // upload to my media
        const fileNameParts = acceptedFile.name.split(".");
        const extension = fileNameParts.pop();
        const name = fileNameParts.join(".");
        const fileName = `${name}.${extension}`;

        let thumbnail;
        const token = getCookieToken();

        if (!acceptedFile?.type.includes("audio/") && !acceptedFile?.type.includes("video/")) {
          try {
            const thumbnailFile = await processThumbnail(newFile, fileResponse?.download_url);

            const thumbnailSigned = await getSignedUrl({
              file: thumbnailFile,
              token,
              folder: METAVERSE_FOLDER,
              apiUrl: configs["CUSTOM_API_URL"]
            });

            await fetch(thumbnailSigned.presigned_url, {
              method: "PUT",
              headers: {
                "Content-Type": "image/png",
                accept: "application/json",
                "x-ms-blob-type": "BlockBlob"
              },
              body: thumbnailFile
            });

            thumbnail = thumbnailSigned.download_url;
          } catch (error) {
            console.error("Error uploading thumbnail", error);
          }
        }

        const entry = {
          name: fileName,
          title: fileName,
          path: fileResponse.key,
          type: getMediaType(extension),
          asset_type: "my-media",
          size: acceptedFile.size
        };

        if (thumbnail) {
          entry.thumbnail = thumbnail;
        }

        const myMediaUrl = `${configs["CUSTOM_API_URL"]}/assets360`;

        // send to my media
        const responseAssets = await fetch(myMediaUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            authorization: `Bearer ${token}`
          },
          body: JSON.stringify(entry)
        });

        if (responseAssets.status === 402) {
          const error = new Error("Storage limit reached");
          error.status = responseAssets.status;
          throw error;
        }

        const responseAssetsJson = await responseAssets.json();
        const downloadUrl = responseAssetsJson?.data?.url || fileResponse?.download_url;
        onFileLoaded(acceptedFiles, downloadUrl, responseAssetsJson);
      } catch (e) {
        console.error("Error uploading file", e);

        if (e.status === 402) {
          APP.scene.emit("storage_limit_reached", {
            maxStorage: 1024
          });
        } else {
          setErrorUpload(true);
        }

        setIndefiniteLoading(false);
        setProgressLoading(0);
      }
    },
    accept: "model/gltf-binary, image/png, image/jpeg, image/gif, application/pdf, video/mp4, audio/mpeg"
  });

  return (
    <>
      <div className={styles.dropzoneCard} {...(!progressLoading && !indefiniteLoading ? getRootProps() : {})}>
        <input {...getInputProps()} style={{ display: "none" }} type="file" />
        <div className={styles.dropzoneContent}>
          {!progressLoading && !indefiniteLoading && (
            <div className={styles.dropzoneContentUpload}>
              <ArrowUploadFilled className={styles.uploadIcon} />
              <p className={styles.firstText}>Only GLB, PNG, JPG, GIF, PDF, MP4 and MP3 files </p>
              <p className={styles.secondText}>Max file size 50 MB</p>
            </div>
          )}
          {progressLoading && !indefiniteLoading ? <ProgressBar value={progressLoading}></ProgressBar> : ""}
          {indefiniteLoading && !progressLoading ? <ProgressBar></ProgressBar> : ""}
          {errorUpload && <p style={{ color: "red", marginBottom: "10px" }}>Error uploading file. Please try again.</p>}
        </div>
      </div>
    </>
  );
}

export default NewDropzone;
